import AssessmentManage from './pages/AssessmentManage';
import AssessmentCreate from './pages/AssessmentCreate';
import Assessors from './pages/Assessors';
import Training from './pages/Training';
import TrainingSemesterPage from './pages/TrainingSemester';
import TrainingSessionPage from './pages/TrainingSessionPage';
import { TrainerHomeRedirect } from './components/training/TrainingShared';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginForm from './components/LoginForm';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Planters from './pages/Planters';
import PlanterPipelineOverview from './pages/PlanterPipelineOverview';
import PlanterDetail from './pages/PlanterDetail';
import PlanterMbcbReportDetail from './pages/PlanterMbcbReportDetail';
import PlanterMbcbReportEditor from './pages/PlanterMbcbReportEditor';
import Reports from './pages/Reports';
import Map from './pages/Map';
import Onboarding from './pages/Onboarding';
import OnboardingDetail from './pages/OnboardingDetail';
import Support from './pages/Support';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Assessments from './pages/Assessments';
import AssessmentDetail from './pages/AssessmentDetail';
import AssessmentReport from './pages/AssessmentReport';
import AssessmentAfterParty from './pages/AssessmentAfterParty';
import AssessmentEventDetails from './pages/AssessmentEventDetails';
import AssessmentFinalReport from './pages/AssessmentFinalReport';
import PortalCandidateProfile from './pages/PortalCandidateProfile';
import TrainersDirectory from './pages/TrainersDirectory';
import CoachesDirectory from './pages/CoachesDirectory';
import ChurchDirectoryPage from './pages/ChurchDirectoryPage';
import Messages from './pages/Messages';
import MessageThread from './pages/MessageThread';
import NotificationsHub from './pages/NotificationsHub';

const TRAINING_ROLES = ['admin', 'staff', 'hub_leader', 'trainer'];

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Completely public routes - no auth context needed */}
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* All other routes wrapped in AuthProvider */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <Routes>
                  {/* Public routes */}
                  <Route
                    path="/login"
                    element={
                      <ProtectedRoute requiresAuth={false}>
                        <LoginForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <ProtectedRoute requiresAuth={false}>
                        <ForgotPassword />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <ProtectedRoute requiresAuth={false}>
                        <ResetPassword />
                      </ProtectedRoute>
                    }
                  />
              
                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <TrainerHomeRedirect><Dashboard /></TrainerHomeRedirect>
                      </ProtectedRoute>
                    }
                  />

                  {/* Planters routes */}
                  <Route
                    path="/planters"
                    element={
                      <ProtectedRoute>
                        <Planters />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planters/pipeline"
                    element={
                      <ProtectedRoute>
                        <PlanterPipelineOverview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planters/:id/reports/:reportId/edit"
                    element={
                      <ProtectedRoute>
                        <PlanterMbcbReportEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planters/:id/reports/:reportId"
                    element={
                      <ProtectedRoute>
                        <PlanterMbcbReportDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planters/:id"
                    element={
                      <ProtectedRoute>
                        <PlanterDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* Map route */}
                  <Route
                    path="/map"
                    element={
                      <ProtectedRoute>
                        <Map />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/trainers"
                    element={
                      <ProtectedRoute>
                        <TrainersDirectory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coaches"
                    element={
                      <ProtectedRoute>
                        <CoachesDirectory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/church-directory"
                    element={
                      <ProtectedRoute>
                        <ChurchDirectoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute>
                        <Messages />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages/:conversationId"
                    element={
                      <ProtectedRoute>
                        <MessageThread />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationsHub />
                      </ProtectedRoute>
                    }
                  />
              
                  {/* Pre-Assessment routes */}
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <Onboarding />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/:id"
                    element={
                      <ProtectedRoute>
                        <OnboardingDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* Training */}
                  <Route path="/training" element={<ProtectedRoute requiredRoles={TRAINING_ROLES}><Training /></ProtectedRoute>} />
                  <Route path="/training/sessions/:sessionId" element={<ProtectedRoute requiredRoles={TRAINING_ROLES}><TrainingSessionPage /></ProtectedRoute>} />
                  <Route path="/training/:id" element={<ProtectedRoute requiredRoles={TRAINING_ROLES}><TrainingSemesterPage /></ProtectedRoute>} />

                  {/* Assessments */}
                  <Route path="/assessments/assessors" element={<ProtectedRoute requiredRoles={['admin', 'assessment-admin']}><Assessors /></ProtectedRoute>} />
                  <Route path="/assessments/new" element={<ProtectedRoute requiredRoles={['admin','assessment-admin']}><AssessmentCreate /></ProtectedRoute>} />
                  <Route path="/assessments/:id/manage" element={<ProtectedRoute requiredRoles={['admin','assessment-admin']}><AssessmentManage /></ProtectedRoute>} />
                  <Route
                    path="/assessments"
                    element={
                      <ProtectedRoute>
                        <Assessments />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id"
                    element={
                      <ProtectedRoute>
                        <AssessmentDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id/reports/:assignmentId"
                    element={
                      <ProtectedRoute>
                        <AssessmentReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id/after-party"
                    element={
                      <ProtectedRoute>
                        <AssessmentAfterParty />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id/event-details"
                    element={
                      <ProtectedRoute>
                        <AssessmentEventDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id/final-reports/:participantId"
                    element={
                      <ProtectedRoute>
                        <AssessmentFinalReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessments/:id/candidates/:candidateId"
                    element={
                      <ProtectedRoute>
                        <PortalCandidateProfile />
                      </ProtectedRoute>
                    }
                  />
                  {/* Default redirect */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  {/* Catch all route */}
                  <Route
                    path="*"
                    element={
                      <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Page Not Found
                          </h2>
                          <p className="text-gray-600 mb-4">
                            The page you're looking for doesn't exist.
                          </p>
                          <button
                            onClick={() => (window.location.href = '/')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Go Home
                          </button>
                        </div>
                      </div>
                    }
                  />
                </Routes>
              </AuthProvider>
            }
          />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
